import {Injectable, Injector} from '@angular/core';
import {ApiService} from '@abstract/api.service';
import {BehaviorSubject} from 'rxjs';
import {Subject} from 'rxjs';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {ErrorLoggerService} from '@services/error-logger.service';
import {takeUntil} from 'rxjs/operators';
import {UploadedFile} from '@interfaces/file.interface';

/**
 * Состояние элемента очереди загрузки
 */
interface XhrDownloadQueueItemState {
    /**
     * Идет ли ожидание генерации файла?
     */
    fetching?: boolean;

    /**
     * Имя выполняемой задачи, какой файл генерируется
     */
    taskName?: string;

    /**
     * Файл уже был загружен?
     */
    downloaded?: boolean;

    /**
     * Текст ошибки генерации файла
     */
    error?: string;

    /**
     * Информация о файле в случае успешной генерации
     */
    file?: UploadedFile;
}

interface DoOptions {
    /**
     * Ссылка, по которой будет генерироваться файл
     */
    url: string;

    /**
     * Как отправлять запрос? Каким методом?
     */
    method?: 'post' | 'get';

    /**
     * Данные для генерации
     */
    data?: object;

    /**
     * Переменная или атрибут, в которую будет записан результат метода do.
     * Нужно для того, чтобы корректно инкапсулировать обработку инициатора вызова метода do внутри самого метода do.
     * Например, клик по кнопке сохраняет вызов service.do() в переменную, которую и должен передавать при вызове
     * (даже если там еще нет значения).
     * Позволяет корректно завершать старые загрузки после вызова новых (см. реализацию do)
     */
    currentQueue?: AjaxDownloadQueueItem;
    /**
     * Имя файла, задачи на выполнение
     */
    taskName?: string;

    /**
     * Выполнять ли автозагрузку файла по окончании генерации?
     */
    autoDownload?: boolean;
}

export type AjaxDownloadQueueItem = BehaviorSubject<XhrDownloadQueueItemState>;

@Injectable({
    providedIn: 'root'
})
export class AjaxDownloadService extends ApiService {

    constructor(
        http: HttpClient,
        logger: ErrorLoggerService,
        injector: Injector,
    ) {
        super(http, logger, injector);

        this.eventsService.logout$.subscribe(() => {
            this.queue.forEach(item => item.complete());
        });
    }

    public newItem$ = new Subject<AjaxDownloadQueueItem>();

    private queue: AjaxDownloadQueueItem[] = [];

    private defaultDoOptions: DoOptions = {
        url: null,
        method: 'get',
        taskName: 'Документ',
        currentQueue: null,
        autoDownload: true,
    };

    /**
     * Фоновая загрузка файла.
     * Создает ссылку, прокидывает в нее файл и кликает на скачивание.
     * @param fileUrl
     */
    static shadowDownload(fileUrl: string) {
        if (!fileUrl) {
            return;
        }

        try {
            const link: HTMLAnchorElement = document.createElement('a');
            document.body.appendChild(link);
            link.href = fileUrl;
            link.download = '';
            // Предотвращем всплытие события, так как это "Фоновая" ссылка и не должна вмешиваться в работу проекта
            link.onclick = (event: Event) => {
                event.stopPropagation();
            };
            link.click();
            document.body.removeChild(link);
        } catch (e) {

        }
    }

    /**
     * Что-то вроде setState из реакта, когда мы мерджим старое и новое состояние
     * @param queue
     * @param newState
     */
    static setQueueState(queue: AjaxDownloadQueueItem, newState: XhrDownloadQueueItemState) {
        const current: XhrDownloadQueueItemState = queue.getValue();
        queue.next(Object.assign({}, current, newState));
    }

    /**
     * Выполнить фоновую генерацию документа
     * @param options
     */
    do(options: DoOptions): AjaxDownloadQueueItem {
        options = Object.assign({}, this.defaultDoOptions, options);

        /**
         * Обработка повторного запроса документа для уже существующего элемента очереди
         */
        if (options.currentQueue) {
            /**
             * Если текущий элемент очереди еще выполняется, то просто дожидаемся ее окончания,
             * тем самым исключая обработку повторных нажатий
             */
            if (options.currentQueue.getValue().fetching) {
                return options.currentQueue;
            }

            /**
             * Если элемент очереди уже дождался генерации, но еще не "Закрыта"
             * (по какой-то причине не скачан файл, не удалена запись из списка), то завершаем ее руками,
             * чтобы в последствии убрать ее и создать новый файл очереди
             */
            if (!options.currentQueue.closed) {
                options.currentQueue.complete();
            }
        }

        /**
         * Создаем новый элемент очереди
         */
        const newQueue$: AjaxDownloadQueueItem = new BehaviorSubject<XhrDownloadQueueItemState>({
            fetching: true,
            taskName: options.taskName,
        });

        /**
         * Поток, чтобы прервать текущую генерацию файла, если элемент очереди завершить раньше времени
         */
        const preventCurrentRequest$ = new Subject();

        this.addQueue(newQueue$);

        const request$ = options.method === 'post' ? this.post({
            url: options.url,
            dataBody: options.data || null
        }) : this.get({
            url: options.url,
            queryParams: options.data || null
        });

        request$.pipe(
            takeUntil(preventCurrentRequest$),
            takeUntil(this.eventsService.logout$),
        ).subscribe((file: UploadedFile) => {
            this.doSuccessHandler(file, newQueue$, options);
        }, (e) => {
            this.doErrorHandler(e, newQueue$);
        });

        /**
         * По завершению работы с текущим элементов очереди (неважно где его завершили), удаляем из очереди
         */
        newQueue$.subscribe({
            complete: () => {
                preventCurrentRequest$.next();
                preventCurrentRequest$.complete();
                this.removeQueue(newQueue$);
            }
        });

        /**
         * Возвращаем элемент очереди, чтобы инициатор мог подписаться на изменения его состояния и показывать изменения в UI
         */
        return newQueue$;
    }


    public handleHttpFailErrors(error: HttpErrorResponse) {
        /**
         * Переопределяем метод, так как Http ошибки будут отражены в уведомлении о генерации файлов
         */
    }

    private doSuccessHandler(file: UploadedFile, queue$: AjaxDownloadQueueItem, options: DoOptions) {

        if (!file) {
            AjaxDownloadService.setQueueState(queue$, {
                fetching: false,
                file: null,
                error: 'Возникла ошибка при генерации документа'
            });
            return;
        }

        const newState: XhrDownloadQueueItemState = {
            fetching: false,
            file: file,
            error: null
        };

        if (options.autoDownload === true) {
            AjaxDownloadService.shadowDownload(file.file_url);
            newState.downloaded = true;
        }

        AjaxDownloadService.setQueueState(queue$, newState);
    }

    private doErrorHandler(e: string[]|null, queue$: AjaxDownloadQueueItem) {
        let errorMessage = 'Возникла ошибка при генерации документа';

        if (e instanceof Array && e.length && typeof e[0] === 'string') {
            errorMessage = e.join(' ');
        }

        AjaxDownloadService.setQueueState(queue$, {
            fetching: false,
            file: null,
            error: errorMessage
        });
    }

    /**
     * Добавить элемент в очередь и уведомить подписчиков, если нужно
     * @param queue
     */
    private addQueue(queue: AjaxDownloadQueueItem) {
        this.newItem$.next(queue);
        this.queue.push(queue);
    }

    /**
     * Удалить файл из очереди
     * @param queue
     */
    private removeQueue(queue: AjaxDownloadQueueItem) {
        this.queue = this.queue.filter(item => item !== queue);
    }
}
